<template lang="html">
  <div class="keeper-settings">
    <div class="section-header">
      <h2>Keepers</h2>

      <div class="actions" v-if="editing">
        <a @click="cancel">Cancel</a>

        <button class="small edit" @click="save">
          Save
        </button>
      </div>
    </div>

    <div class="display" v-if="!editing">
      <div class="team" v-for="team in teamKeepers" :key="team.id">
        <h4>{{ team.name }}</h4>

        <p class="subtle" v-if="team.keepers.length === 0">No keepers</p>

        <div class="keeper" v-for="({ round, player }, i) in team.keepers" :key="i">
          <b>{{ player.label }}</b>
          <span>({{ round.label }})</span>
        </div>

        <a v-if="isCommissioner" @click="editTeamKeepers(team)">
          Edit keepers
        </a>
      </div>
    </div>

    <div class="editing" v-else>
      <h3>{{ activeTeam.name }}</h3><br />

      <div class="edit-keeper input-group" v-for="(keeper, i) in keepers" :key="i">
        <multiselect
          v-model="keepers[i].round"
          :options="roundOptions"
          label="label"
          placeholder="Round"
        />

        <multiselect
          v-model="keepers[i].player"
          :options="playerOptions"
          label="label"
          placeholder="Player"
        />

        <div @click="removeKeeper(i)">
          <i class="fas fa-trash" />
        </div>
      </div>

      <p class="add-keeper">
        <a @click="addKeeper">+ Add keeper</a>
      </p>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import clonedeep from 'lodash.clonedeep';

export default {
  data() {
    return {
      editing: false,
      activeTeam: null,
      keepers: [],
    };
  },

  computed: {
    ...mapState({
      draft: ({ draft }) => draft.draft,
    }),

    ...mapGetters({
      isCommissioner: 'draft/isCommissioner',
      availablePlayers: 'draft/availablePlayers',
      keepersByTeam: 'draft/keepersByTeam',
      draftablePositions: 'draft/draftablePositions',
    }),

    teamKeepers() {
      return this.keepersByTeam.map(({
        id,
        name,
        keepers,
      }) => {
        return {
          id,
          name,
          keepers: keepers.map((keeper) => {
            return {
              round: {
                value: keeper.round,
                label: keeper.round,
              },
              player: {
                value: keeper.id,
                label: `${keeper.position} ${keeper.name}, ${keeper.team}`,
              },
            };
          }),
        };
      });
    },

    roundOptions() {
      return this.$store.getters['draft/tradeKeeperRoundOptions'](this.activeTeam, true)
        .filter(({ value }) => {
          return !this.keepers.find(({ round }) => round && value === round.value);
        });
    },

    playerOptions() {
      return this.availablePlayers
        .map((player) => {
          return {
            value: player.id,
            label: `${player.position} ${player.name}, ${player.team}`,
            position: player.position,
          };
        })
        .filter(({ position, value }) => {
          const alreadyKept = this.keepers.find(({ player }) => player && value === player.value);
          const posAllowed = this.draftablePositions.find(({ position: pos }) => pos === position);

          return !alreadyKept && posAllowed;
        });
    },
  },

  methods: {
    editTeamKeepers(team) {
      this.activeTeam = team;
      this.keepers = clonedeep(this.teamKeepers.find(({ id }) => id === team.id).keepers);
      this.addKeeper();
      this.editing = true;
    },

    addKeeper() {
      this.keepers.push({ round: null, player: null });
    },

    cancel() {
      this.editing = false;
    },

    save() {
      const saveKeepers = this.keepers
        .filter(({ round, player }) => {
          return round && round.value && player && player.value;
        })
        .map(({ round, player }) => {
          return { r: round.value, p: player.value };
        });

      this.$store.dispatch('draft/SAVE_KEEPERS', { teamId: this.activeTeam.id, keepers: saveKeepers })
        .then(() => {
          this.$toast({ message: 'Keepers saved' });
        });
      this.editing = false;
    },

    removeKeeper(i) {
      this.keepers.splice(i, 1);
    },
  },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @import "./settings";

  .keeper-settings {
    @include settingSection;
  }

  .add-keeper {
    margin-top: $gp * 2;
  }

  .keeper,
  .edit-keeper {
    margin-bottom: $gp;
  }

  .team {
    margin-bottom: $gp * 2;
  }
</style>
